import { TourDefinition, TourKey } from "../models/tour-step.model";

/**
 * Single source of truth for every in-app interface guide.
 * Each entry maps a TourKey to the ordered list of DOM spots it walks the
 * user through. Selectors point at existing page-shell classes so no visual
 * markup had to change; steps that don't exist yet in the DOM (e.g. an
 * account with no WhatsApp number connected) are dropped at run time by
 * OnboardingTourService instead of breaking the tour.
 */
export const ONBOARDING_TOURS: Record<TourKey, TourDefinition> = {
  teamInbox: {
    key: "teamInbox",
    steps: [
      {
        element: ".ti-page__list",
        titleKey: "ONBOARDING_TOUR.TEAM_INBOX.LIST.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEAM_INBOX.LIST.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: ".cl-header__filter-wrap",
        titleKey: "ONBOARDING_TOUR.TEAM_INBOX.FILTERS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEAM_INBOX.FILTERS.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".cl-create-btn",
        titleKey: "ONBOARDING_TOUR.TEAM_INBOX.CREATE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEAM_INBOX.CREATE.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
    ],
  },

  teamInboxConversation: {
    key: "teamInboxConversation",
    steps: [
      {
        element: ".conversation-session-banner",
        titleKey: "ONBOARDING_TOUR.TEAM_INBOX_CONVERSATION.WINDOW.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEAM_INBOX_CONVERSATION.WINDOW.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".ti-tools-rail",
        titleKey: "ONBOARDING_TOUR.TEAM_INBOX_CONVERSATION.RAIL.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEAM_INBOX_CONVERSATION.RAIL.DESCRIPTION",
        side: "left",
        align: "start",
      },
    ],
  },

  contacts: {
    key: "contacts",
    steps: [
      {
        element: ".cp-toolbar-btn--accent",
        titleKey: "ONBOARDING_TOUR.CONTACTS.CREATE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS.CREATE.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
      {
        element: ".cp-filter-icon",
        titleKey: "ONBOARDING_TOUR.CONTACTS.FILTER.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS.FILTER.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".cp-table-area",
        titleKey: "ONBOARDING_TOUR.CONTACTS.LIST.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS.LIST.DESCRIPTION",
        side: "top",
        align: "start",
      },
      {
        element: '[data-tour="contact-row-edit"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS.EDIT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS.EDIT.DESCRIPTION",
        side: "top",
        align: "start",
      },
      {
        element: '[data-tour="contact-import"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS.IMPORT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS.IMPORT.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
    ],
  },

  contactsCreate: {
    key: "contactsCreate",
    steps: [
      {
        element: '[data-tour="contact-form-name"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS_CREATE.NAME.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_CREATE.NAME.DESCRIPTION",
        side: "left",
        align: "start",
      },
      {
        element: '[data-tour="contact-form-phone"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS_CREATE.PHONE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_CREATE.PHONE.DESCRIPTION",
        side: "left",
        align: "start",
      },
      {
        element: '[data-tour="contact-form-qualified"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS_CREATE.QUALIFIED.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_CREATE.QUALIFIED.DESCRIPTION",
        side: "left",
        align: "start",
      },
      {
        element: ".cf-attrs",
        titleKey: "ONBOARDING_TOUR.CONTACTS_CREATE.ATTRIBUTES.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_CREATE.ATTRIBUTES.DESCRIPTION",
        side: "left",
        align: "start",
      },
    ],
  },

  contactsAttributes: {
    key: "contactsAttributes",
    steps: [
      {
        element: ".acp__list",
        titleKey: "ONBOARDING_TOUR.CONTACTS_ATTRIBUTES.LIST.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_ATTRIBUTES.LIST.DESCRIPTION",
        side: "left",
        align: "start",
      },
      {
        element: ".acp__footer",
        titleKey: "ONBOARDING_TOUR.CONTACTS_ATTRIBUTES.ADD.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_ATTRIBUTES.ADD.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  // Every import shows the same screen: an example file, the template, the drop zone, where to get the file, the history.
  contactsImport: {
    key: "contactsImport",
    steps: [
      {
        element: '[data-tour="import-drop"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS_IMPORT.DROP.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_IMPORT.DROP.DESCRIPTION",
        side: "top",
        align: "start",
      },
      {
        element: '[data-tour="import-template"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS_IMPORT.TEMPLATE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_IMPORT.TEMPLATE.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: '[data-tour="import-example"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS_IMPORT.EXAMPLE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_IMPORT.EXAMPLE.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: '[data-tour="import-history"]',
        titleKey: "ONBOARDING_TOUR.CONTACTS_IMPORT.HISTORY.TITLE",
        descriptionKey: "ONBOARDING_TOUR.CONTACTS_IMPORT.HISTORY.DESCRIPTION",
        side: "left",
        align: "start",
      },
    ],
  },

  importWizard: {
    key: "importWizard",
    steps: [
      {
        element: '[data-tour="import-drop"]',
        titleKey: "ONBOARDING_TOUR.IMPORT_WIZARD.DROP.TITLE",
        descriptionKey: "ONBOARDING_TOUR.IMPORT_WIZARD.DROP.DESCRIPTION",
        side: "top",
        align: "start",
      },
      {
        element: '[data-tour="import-template"]',
        titleKey: "ONBOARDING_TOUR.IMPORT_WIZARD.TEMPLATE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.IMPORT_WIZARD.TEMPLATE.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: '[data-tour="import-example"]',
        titleKey: "ONBOARDING_TOUR.IMPORT_WIZARD.EXAMPLE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.IMPORT_WIZARD.EXAMPLE.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: '[data-tour="import-history"]',
        titleKey: "ONBOARDING_TOUR.IMPORT_WIZARD.HISTORY.TITLE",
        descriptionKey: "ONBOARDING_TOUR.IMPORT_WIZARD.HISTORY.DESCRIPTION",
        side: "left",
        align: "start",
      },
    ],
  },

  templates: {
    key: "templates",
    steps: [
      {
        element: ".thub-header",
        titleKey: "ONBOARDING_TOUR.TEMPLATES.HEADER.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATES.HEADER.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".thub-header__cta",
        titleKey: "ONBOARDING_TOUR.TEMPLATES.NEW.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATES.NEW.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
      {
        element: ".tpage__toolbar",
        titleKey: "ONBOARDING_TOUR.TEMPLATES.FILTER.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATES.FILTER.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".tpage__table",
        titleKey: "ONBOARDING_TOUR.TEMPLATES.TABLE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATES.TABLE.DESCRIPTION",
        side: "top",
        align: "start",
      },
      {
        element: ".ttbl__send",
        titleKey: "ONBOARDING_TOUR.TEMPLATES.SEND.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATES.SEND.DESCRIPTION",
        side: "left",
        align: "start",
      },
    ],
  },

  templateCreate: {
    key: "templateCreate",
    steps: [
      {
        element: '[data-tour="template-form-name"]',
        titleKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.NAME.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.NAME.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: '[data-tour="template-form-body"]',
        titleKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.BODY.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.BODY.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: '[data-tour="template-form-variables"]',
        titleKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.VARIABLES.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.VARIABLES.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: ".trf-buttons-section",
        titleKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.BUTTONS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.BUTTONS.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: ".tpl-preview-card",
        titleKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.PREVIEW.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.PREVIEW.DESCRIPTION",
        side: "left",
        align: "start",
      },
      {
        element: ".tpl-page-footer",
        titleKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.SUBMIT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.TEMPLATE_CREATE.SUBMIT.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  flows: {
    key: "flows",
    steps: [
      {
        element: ".flow-search",
        titleKey: "ONBOARDING_TOUR.FLOWS.HEADER.TITLE",
        descriptionKey: "ONBOARDING_TOUR.FLOWS.HEADER.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".flow-table-area",
        titleKey: "ONBOARDING_TOUR.FLOWS.SEARCH.TITLE",
        descriptionKey: "ONBOARDING_TOUR.FLOWS.SEARCH.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  flowCreate: {
    key: "flowCreate",
    steps: [
      {
        element: ".tpl-card--form",
        titleKey: "ONBOARDING_TOUR.FLOW_CREATE.FORM.TITLE",
        descriptionKey: "ONBOARDING_TOUR.FLOW_CREATE.FORM.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: ".tpl-preview-card",
        titleKey: "ONBOARDING_TOUR.FLOW_CREATE.PREVIEW.TITLE",
        descriptionKey: "ONBOARDING_TOUR.FLOW_CREATE.PREVIEW.DESCRIPTION",
        side: "left",
        align: "start",
      },
      {
        element: ".cp-header__right",
        titleKey: "ONBOARDING_TOUR.FLOW_CREATE.SUBMIT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.FLOW_CREATE.SUBMIT.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
    ],
  },

  generalSettings: {
    key: "generalSettings",
    steps: [
      {
        element: ".gpp-header",
        titleKey: "ONBOARDING_TOUR.GENERAL_SETTINGS.HEADER.TITLE",
        descriptionKey: "ONBOARDING_TOUR.GENERAL_SETTINGS.HEADER.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".gpp-rail",
        titleKey: "ONBOARDING_TOUR.GENERAL_SETTINGS.NAV.TITLE",
        descriptionKey: "ONBOARDING_TOUR.GENERAL_SETTINGS.NAV.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: ".gpp-main",
        titleKey: "ONBOARDING_TOUR.GENERAL_SETTINGS.PANEL.TITLE",
        descriptionKey: "ONBOARDING_TOUR.GENERAL_SETTINGS.PANEL.DESCRIPTION",
        side: "left",
        align: "start",
      },
    ],
  },

  generalSettingsWhatsapp: {
    key: "generalSettingsWhatsapp",
    steps: [
      {
        element: ".gp-stage",
        titleKey: "ONBOARDING_TOUR.GENERAL_SETTINGS_WHATSAPP.STAGE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.GENERAL_SETTINGS_WHATSAPP.STAGE.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  metaConnection: {
    key: "metaConnection",
    steps: [
      {
        element: ".mw-tabs",
        titleKey: "ONBOARDING_TOUR.META_OVERVIEW.JOURNEY.TITLE",
        descriptionKey: "ONBOARDING_TOUR.META_OVERVIEW.JOURNEY.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".mw-panel",
        titleKey: "ONBOARDING_TOUR.META_CONNECTION.PANEL.TITLE",
        descriptionKey: "ONBOARDING_TOUR.META_CONNECTION.PANEL.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  metaAccount: {
    key: "metaAccount",
    steps: [
      {
        element: ".mw-panel",
        titleKey: "ONBOARDING_TOUR.META_ACCOUNT.PANEL.TITLE",
        descriptionKey: "ONBOARDING_TOUR.META_ACCOUNT.PANEL.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  metaPhones: {
    key: "metaPhones",
    steps: [
      {
        element: ".mw-panel",
        titleKey: "ONBOARDING_TOUR.META_PHONES.PANEL.TITLE",
        descriptionKey: "ONBOARDING_TOUR.META_PHONES.PANEL.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  metaCatalog: {
    key: "metaCatalog",
    steps: [
      {
        element: ".mw-panel",
        titleKey: "ONBOARDING_TOUR.META_CATALOG.PANEL.TITLE",
        descriptionKey: "ONBOARDING_TOUR.META_CATALOG.PANEL.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  broadcast: {
    key: "broadcast",
    steps: [
      {
        element: ".cp-toolbar-btn.cp-toolbar-btn--accent",
        titleKey: "ONBOARDING_TOUR.BROADCAST.NEW.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST.NEW.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
      {
        element: ".cp-filter-icon",
        titleKey: "ONBOARDING_TOUR.BROADCAST.FILTER.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST.FILTER.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".bh-table-card",
        titleKey: "ONBOARDING_TOUR.BROADCAST.TABLE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST.TABLE.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  broadcastCreate: {
    key: "broadcastCreate",
    steps: [
      {
        element: "#title",
        titleKey: "ONBOARDING_TOUR.BROADCAST_CREATE.NAME.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST_CREATE.NAME.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".bc-wstep__body--info .bc-nav-btn--accent",
        titleKey: "ONBOARDING_TOUR.BROADCAST_CREATE.NEXT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST_CREATE.NEXT.DESCRIPTION",
        side: "top",
        align: "end",
      },
    ],
  },

  broadcastCreateSchedule: {
    key: "broadcastCreateSchedule",
    steps: [
      {
        element: ".bc-wstep__body--schedule .bc-schedule-options",
        titleKey: "ONBOARDING_TOUR.BROADCAST_CREATE.SCHEDULE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST_CREATE.SCHEDULE.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  broadcastCreateAudience: {
    key: "broadcastCreateAudience",
    steps: [
      {
        element: ".bc-wstep__body--audience",
        titleKey: "ONBOARDING_TOUR.BROADCAST_CREATE.AUDIENCE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST_CREATE.AUDIENCE.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  broadcastCreateMessage: {
    key: "broadcastCreateMessage",
    steps: [
      {
        element: ".bc-wstep__body--message",
        titleKey: "ONBOARDING_TOUR.BROADCAST_CREATE.MESSAGE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST_CREATE.MESSAGE.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  broadcastCreateReview: {
    key: "broadcastCreateReview",
    steps: [
      {
        element: ".bc-recap",
        titleKey: "ONBOARDING_TOUR.BROADCAST_CREATE.REVIEW.TITLE",
        descriptionKey: "ONBOARDING_TOUR.BROADCAST_CREATE.REVIEW.DESCRIPTION",
        side: "top",
        align: "start",
      },
    ],
  },

  // ── Payment ────────────────────────────────────────────────────────────────
  paymentLinks: {
    key: "paymentLinks",
    steps: [
      {
        element: ".cp-toolbar-btn--accent",
        titleKey: "ONBOARDING_TOUR.PAYMENT_LINKS.CREATE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_LINKS.CREATE.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
      {
        element: ".cp-filter-icon",
        titleKey: "ONBOARDING_TOUR.PAYMENT_LINKS.FILTER.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_LINKS.FILTER.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: '[data-tour="payment-link-status"]',
        titleKey: "ONBOARDING_TOUR.PAYMENT_LINKS.STATUS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_LINKS.STATUS.DESCRIPTION",
        side: "top",
        align: "start",
      },
      {
        element: '[data-tour="payment-link-edit"]',
        titleKey: "ONBOARDING_TOUR.PAYMENT_LINKS.EDIT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_LINKS.EDIT.DESCRIPTION",
        side: "top",
        align: "end",
      },
      {
        element: '[data-tour="payment-link-more"]',
        titleKey: "ONBOARDING_TOUR.PAYMENT_LINKS.MORE.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_LINKS.MORE.DESCRIPTION",
        side: "top",
        align: "end",
      },
      {
        element: '[data-tour="payment-link-more"]',
        titleKey: "ONBOARDING_TOUR.PAYMENT_LINKS.AUTHORIZATIONS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_LINKS.AUTHORIZATIONS.DESCRIPTION",
        side: "top",
        align: "end",
      },
    ],
  },

  paymentLinkCreate: {
    key: "paymentLinkCreate",
    steps: [
      {
        element: ".plf__grid",
        titleKey: "ONBOARDING_TOUR.PAYMENT_LINK_CREATE.FORM.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_LINK_CREATE.FORM.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: ".pav-footer",
        titleKey: "ONBOARDING_TOUR.PAYMENT_LINK_CREATE.SUBMIT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_LINK_CREATE.SUBMIT.DESCRIPTION",
        side: "top",
        align: "end",
      },
    ],
  },

  paymentAuthorizations: {
    key: "paymentAuthorizations",
    steps: [
      {
        element: '[data-tour="payment-auth-status"]',
        titleKey: "ONBOARDING_TOUR.PAYMENT_AUTHORIZATIONS.STATUS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_AUTHORIZATIONS.STATUS.DESCRIPTION",
        side: "top",
        align: "start",
      },
      {
        element: '[data-tour="payment-auth-actions"]',
        titleKey: "ONBOARDING_TOUR.PAYMENT_AUTHORIZATIONS.ACTIONS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_AUTHORIZATIONS.ACTIONS.DESCRIPTION",
        side: "top",
        align: "end",
      },
    ],
  },

  paymentSavedCards: {
    key: "paymentSavedCards",
    steps: [
      {
        element: ".cp-filter-icon",
        titleKey: "ONBOARDING_TOUR.PAYMENT_SAVED_CARDS.FILTER.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_SAVED_CARDS.FILTER.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: '[data-tour="saved-card-status"]',
        titleKey: "ONBOARDING_TOUR.PAYMENT_SAVED_CARDS.STATUS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_SAVED_CARDS.STATUS.DESCRIPTION",
        side: "top",
        align: "start",
      },
      {
        element: '[data-tour="saved-card-authorizations"]',
        titleKey: "ONBOARDING_TOUR.PAYMENT_SAVED_CARDS.AUTHORIZATIONS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.PAYMENT_SAVED_CARDS.AUTHORIZATIONS.DESCRIPTION",
        side: "top",
        align: "end",
      },
    ],
  },

  // ── Getting started: points at the setup checklist in the header ──────────
  setupSteps: {
    key: "setupSteps",
    steps: [
      {
        element: '[data-tour="onboarding-rail"]',
        titleKey: "ONBOARDING_TOUR.SETUP_STEPS.PILL.TITLE",
        descriptionKey: "ONBOARDING_TOUR.SETUP_STEPS.PILL.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
    ],
  },

  // ── Restaurant: tables and their QR codes ──────────────────────────────────
  diningTables: {
    key: "diningTables",
    steps: [
      {
        element: '[data-tour="tables-add"]',
        titleKey: "ONBOARDING_TOUR.DINING_TABLES.ADD.TITLE",
        descriptionKey: "ONBOARDING_TOUR.DINING_TABLES.ADD.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
      {
        element: ".dt-filters",
        titleKey: "ONBOARDING_TOUR.DINING_TABLES.AREAS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.DINING_TABLES.AREAS.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: ".dt-card__qr",
        titleKey: "ONBOARDING_TOUR.DINING_TABLES.QR.TITLE",
        descriptionKey: "ONBOARDING_TOUR.DINING_TABLES.QR.DESCRIPTION",
        side: "right",
        align: "start",
      },
      {
        element: '[data-tour="tables-print"]',
        titleKey: "ONBOARDING_TOUR.DINING_TABLES.PRINT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.DINING_TABLES.PRINT.DESCRIPTION",
        side: "bottom",
        align: "end",
      },
    ],
  },

  // ── Restaurant: delivery rings around each sales point ─────────────────────
  deliveryRings: {
    key: "deliveryRings",
    steps: [
      {
        element: '[data-tour="rings-points"]',
        titleKey: "ONBOARDING_TOUR.DELIVERY_RINGS.POINTS.TITLE",
        descriptionKey: "ONBOARDING_TOUR.DELIVERY_RINGS.POINTS.DESCRIPTION",
        side: "bottom",
        align: "start",
      },
      {
        element: '[data-tour="rings-map"]',
        titleKey: "ONBOARDING_TOUR.DELIVERY_RINGS.MAP.TITLE",
        descriptionKey: "ONBOARDING_TOUR.DELIVERY_RINGS.MAP.DESCRIPTION",
        side: "right",
        align: "center",
      },
      {
        element: '[data-tour="rings-default"]',
        titleKey: "ONBOARDING_TOUR.DELIVERY_RINGS.DEFAULT.TITLE",
        descriptionKey: "ONBOARDING_TOUR.DELIVERY_RINGS.DEFAULT.DESCRIPTION",
        side: "left",
        align: "start",
      },
      {
        element: '[data-tour="rings-list"]',
        titleKey: "ONBOARDING_TOUR.DELIVERY_RINGS.LIST.TITLE",
        descriptionKey: "ONBOARDING_TOUR.DELIVERY_RINGS.LIST.DESCRIPTION",
        side: "left",
        align: "start",
      },
    ],
  },
};
